import type { FitnessDay, FitnessResponse, FormZone, Sport } from '@painpal/contracts';
import { formZone, SPORTS } from '@painpal/core';
import type { SportFilter } from '../../api/keys';
import { AXIS_TEXT, chartTooltip, dataIndexOf } from '../../lib/chartTheme';
import type {
  BarSeriesOption, ECOption, GridComponentOption, LineSeriesOption, XAXisComponentOption,
  YAXisComponentOption,
} from '../../lib/echarts';
import { formatMonthDay, formatSigned, formatWeekdayMonthDay } from '../../lib/format';
import { alpha, chartMono, colors, SPORT_COLORS } from '../../theme/tokens';
import type { Variant } from '../../ui/useMediaQuery';
import { peakIndex } from './fitnessStats';
import { filterLabel } from './sportFilterModel';

export const FITNESS_CHART_HEIGHT = 560;
export const FITNESS_CHART_HEIGHT_PHONE = 464;
const LEFT = 48;
const RIGHT = 104;
const PHONE_LEFT = 30;
const PHONE_RIGHT = 8;
export const TRACKS = [
  { top: 8, height: 260 },
  { top: 286, height: 120 },
  { top: 424, height: 110 },
] as const;
// Form→Load is 42 apart (not 14) so the HTML zone key fits under the Form plot in up to two rows.
export const PHONE_TRACKS = [
  { top: 4, height: 180 },
  { top: 198, height: 96 },
  { top: 336, height: 110 },
] as const;
export const PHONE_ZONE_KEY_TOP = 302;
const FORM_TICKS = [-30, 0, 30];

export const ZONE_FILL: Record<FormZone, string> = {
  fresh: colors.green, neutral: colors.paper, productive: colors.amber, overreaching: colors.hot,
};

export const ZONE_BAND: Record<FormZone, string> = {
  fresh: alpha.freshBand, neutral: 'transparent', productive: alpha.productiveBand, overreaching: alpha.overreachingBand,
};

const round1 = (n: number): number => Math.round(n * 10) / 10;

type MarkPointItem = NonNullable<NonNullable<LineSeriesOption['markPoint']>['data']>[number];
type MarkAreaData = NonNullable<NonNullable<LineSeriesOption['markArea']>['data']>;
type ZonePair = Extract<MarkAreaData[number], readonly unknown[]>;

export interface FormRange { min: number; max: number }

export function formAxisRange(days: FitnessDay[]): FormRange {
  const forms = days.map((d) => d.form);
  return { min: Math.floor(Math.min(-40, ...forms) / 10) * 10, max: Math.ceil(Math.max(30, ...forms) / 10) * 10 };
}

export const categoryLabel = (value: string | number): string => formatMonthDay(String(value));

export function grids(variant: Variant = 'desktop'): GridComponentOption[] {
  const phone = variant === 'phone';
  return (phone ? PHONE_TRACKS : TRACKS).map((t): GridComponentOption => ({
    left: phone ? PHONE_LEFT : LEFT, right: phone ? PHONE_RIGHT : RIGHT, top: t.top, height: t.height,
  }));
}

// Labels step back from today in even gaps (at most `labels` before it). The axis's last day keeps its label
// unless `endLabel` is false.
export function labelInterval(count: number, todayIndex = count - 1, labels = 3, endLabel = true): (index: number) => boolean {
  const step = Math.max(1, Math.floor(todayIndex / labels));
  return (index) => {
    const back = todayIndex - index;
    return (index === count - 1 && endLabel) || (back >= 0 && back % step === 0 && back <= labels * step);
  };
}

// A function interval forces its labels, so the plan's end label is only kept where it can't print over TODAY:
// right-aligned it reaches ~33 px back from its day, and TODAY ~15 px forward from today; 56 leaves a visible gap.
const END_LABEL_CLEARANCE = 56;

// Whether the plan's end label clears TODAY on a chart `chartWidth` px wide (0 until measured, which never fits).
export function endLabelFits(days: FitnessDay[], forecast: FitnessDay[], variant: Variant, chartWidth: number): boolean {
  const count = chartDates(days, forecast).length;
  const plot = chartWidth - (variant === 'phone' ? PHONE_LEFT + PHONE_RIGHT : LEFT + RIGHT);
  return count > days.length && ((count - days.length) * plot) / count >= END_LABEL_CLEARANCE;
}

// Phone always labels TODAY; desktop keeps ECharts' own labels until a forecast pushes today off the axis end.
function dayLabels(dates: string[], variant: Variant, todayIndex: number, endLabel: boolean) {
  if (variant !== 'phone' && todayIndex === dates.length - 1) return {};
  return {
    interval: labelInterval(dates.length, todayIndex, variant === 'phone' ? 3 : 8, endLabel),
    alignMaxLabel: 'right' as const,
    formatter: (value: string, index: number) => (index === todayIndex ? '{today|TODAY}' : categoryLabel(value)),
    rich: { today: { color: colors.hot, fontFamily: chartMono, fontSize: 9 } },
  };
}

export function xAxes(dates: string[], variant: Variant = 'desktop', todayIndex = dates.length - 1, endLabel = false): XAXisComponentOption[] {
  return TRACKS.map((_, gridIndex): XAXisComponentOption => ({
    type: 'category',
    gridIndex,
    data: dates,
    boundaryGap: true,
    axisTick: { show: false },
    axisLine: { show: true, onZero: true, lineStyle: { color: gridIndex === 2 ? colors.ink : alpha.zeroLine } },
    axisLabel: {
      show: gridIndex === 2, ...AXIS_TEXT, formatter: categoryLabel,
      ...dayLabels(dates, variant, todayIndex, endLabel),
    },
  }));
}

export function yAxes(range: FormRange): YAXisComponentOption[] {
  const shared = { axisLine: { show: false }, axisTick: { show: false }, splitLine: { lineStyle: { color: colors.line } } };
  return [
    { ...shared, type: 'value', gridIndex: 0, min: 0, splitNumber: 3, axisLabel: AXIS_TEXT },
    {
      ...shared, type: 'value', gridIndex: 1, min: range.min, max: range.max,
      axisTick: { show: false, customValues: FORM_TICKS },
      axisLabel: { ...AXIS_TEXT, formatter: formatSigned, customValues: FORM_TICKS },
    },
    {
      ...shared, type: 'value', gridIndex: 2, min: 0, splitNumber: 2, axisLabel: AXIS_TEXT,
      name: 'LOAD', nameLocation: 'start', nameGap: 6, nameTextStyle: AXIS_TEXT,
    },
  ];
}

function dot(date: string, value: number, fill: string, ring: number, size: number): MarkPointItem {
  return {
    name: 'dot', coord: [date, value], symbol: 'circle', symbolSize: size,
    itemStyle: { color: fill, borderColor: colors.ink, borderWidth: ring }, label: { show: false },
  };
}

export function fitnessLines(days: FitnessDay[], variant: Variant = 'desktop'): LineSeriesOption[] {
  const size = variant === 'phone' ? 10 : 12;
  const peak = days[peakIndex(days)];
  return [
    {
      name: 'Fitness', type: 'line', xAxisIndex: 0, yAxisIndex: 0, showSymbol: false,
      data: days.map((d) => round1(d.fitness)),
      lineStyle: { color: colors.ink, width: 3.5, cap: 'round', join: 'round' },
      itemStyle: { color: colors.ink },
      areaStyle: { color: alpha.fitnessArea },
      markPoint: { silent: true, data: peak === undefined ? [] : [dot(peak.date, round1(peak.fitness), colors.paper, variant === 'phone' ? 2.5 : 3, size)] },
    },
    {
      name: 'Fatigue', type: 'line', xAxisIndex: 0, yAxisIndex: 0, showSymbol: false,
      data: days.map((d) => round1(d.fatigue)),
      lineStyle: { color: colors.hot, width: 2 },
      itemStyle: { color: colors.hot },
    },
  ];
}

function zone(name: string, color: string, from: number, to: number, variant: Variant): ZonePair {
  return [
    {
      name, yAxis: from, itemStyle: { color },
      label: variant === 'phone'
        ? { show: false }
        : { show: true, position: 'right', distance: 8, color: colors.textSecondary, fontFamily: chartMono, fontSize: 8 },
    },
    { yAxis: to },
  ];
}

export function zoneAreas(range: FormRange, variant: Variant = 'desktop'): ZonePair[] {
  return [
    zone('FRESH', ZONE_BAND.fresh, 5, range.max, variant),
    zone('NEUTRAL', ZONE_BAND.neutral, -10, 5, variant),
    zone('PRODUCTIVE', ZONE_BAND.productive, -30, -10, variant),
    zone('OVERREACHING', ZONE_BAND.overreaching, range.min, -30, variant),
  ];
}

export function formLine(days: FitnessDay[], range: FormRange, variant: Variant = 'desktop'): LineSeriesOption {
  const today = days.at(-1);
  return {
    name: 'Form', type: 'line', xAxisIndex: 1, yAxisIndex: 1, showSymbol: false,
    data: days.map((d) => round1(d.form)),
    lineStyle: { color: colors.ink, width: 2 },
    itemStyle: { color: colors.ink },
    markArea: { silent: true, data: zoneAreas(range, variant) },
    markPoint: {
      silent: true,
      data: today === undefined ? [] : [dot(today.date, round1(today.form), ZONE_FILL[formZone(today.form)], variant === 'phone' ? 2 : 2.5, variant === 'phone' ? 10 : 12)],
    },
  };
}

export type HistoryDay = FitnessResponse['days'][number];

const NO_LOAD: Record<Sport, number> = { ride: 0, run: 0, swim: 0, walk: 0, strength: 0, other: 0 };

// An API from before sports sends no split (mid-deploy); all its Load was rides'.
function sportLoads(day: HistoryDay): Record<Sport, number> {
  const split = Object.keys(day.loadBySport).length === 0 && day.load > 0 ? { ride: day.load } : day.loadBySport;
  return { ...NO_LOAD, ...split };
}

export const dayLoad = (day: HistoryDay, sport: SportFilter): number => (sport === 'all' ? day.load : sportLoads(day)[sport]);

// The sports the ALL stack draws: those with Load in range, bottom up in SPORTS order.
export const loadSports = (days: readonly HistoryDay[]): Sport[] => SPORTS.filter((s) => days.some((d) => sportLoads(d)[s] > 0));

// Largest remainder, so a day's segments add up to its rounded Load, as the tooltip reads it.
function roundShares(loads: Record<Sport, number>, sports: readonly Sport[]): Record<Sport, number> {
  const spare = Math.round(sports.reduce((sum, s) => sum + loads[s], 0)) - sports.reduce((sum, s) => sum + Math.floor(loads[s]), 0);
  const bumped = [...sports].sort((a, b) => (loads[b] % 1) - (loads[a] % 1)).slice(0, spare);
  const rounded = { ...NO_LOAD };
  for (const s of sports) rounded[s] = Math.floor(loads[s]) + (bumped.includes(s) ? 1 : 0);
  return rounded;
}

// One stacked series per drawn sport. Filtered to one sport, its days without that sport keep a hairline, as the
// planned bars do for Rest.
export function loadBars(days: readonly HistoryDay[], variant: Variant = 'desktop', sport: SportFilter = 'all'): BarSeriesOption[] {
  const filtered = sport !== 'all';
  const sports = filtered ? [sport] : loadSports(days);
  const rows = days.map((d) => roundShares(sportLoads(d), sports));
  return sports.map((s): BarSeriesOption => ({
    name: `${s} load`, type: 'bar', stack: 'load', xAxisIndex: 2, yAxisIndex: 2, barCategoryGap: variant === 'phone' ? 2 : '25%',
    ...(filtered ? { barMinHeight: 2 } : {}),
    data: rows.map((row) => (filtered && row[s] === 0
      ? { value: 0, itemStyle: { color: colors.line } }
      : { value: row[s], itemStyle: { color: SPORT_COLORS[s] } })),
  }));
}

export function tooltipHtml(day: FitnessDay, planned = false, loadLabel = 'LOAD'): string {
  return [
    `<b>${formatWeekdayMonthDay(day.date)}${planned ? ' · PLAN' : ''}</b>`,
    `FITNESS ${Math.round(day.fitness)}`,
    `FATIGUE ${Math.round(day.fatigue)}`,
    `FORM ${formatSigned(day.form)}`,
    `${planned ? 'PLANNED LOAD' : loadLabel} ${Math.round(day.load)}`,
  ].join('<br/>');
}

export const loadLabel = (sport: SportFilter): string => (sport === 'all' ? 'LOAD' : `${filterLabel(sport)} LOAD`);

// An unridden today is the forecast's first day, so its planned Load joins today's actual values. Filtered, a past day
// reads that sport's Load, as its bar shows it.
export function fitnessTooltip(days: HistoryDay[], forecast: FitnessDay[] = [], sport: SportFilter = 'all'): (params: unknown) => string {
  const ahead = futureOf(days, forecast);
  const todayPlan = forecast.find((d) => d.date === days.at(-1)?.date);
  return (params) => {
    const index = dataIndexOf(params);
    const past = days[index];
    if (past === undefined) {
      const future = ahead[index - days.length];
      return future === undefined ? '' : tooltipHtml(future, true);
    }
    const html = tooltipHtml({ ...past, load: dayLoad(past, sport) }, false, loadLabel(sport));
    return index === days.length - 1 && todayPlan !== undefined ? `${html}<br/>PLANNED LOAD ${Math.round(todayPlan.load)}` : html;
  };
}

const FORECAST_DASH = [5, 3];

// The forecast days after today. A forecast that starts today (planned, not yet ridden) keeps today's history point,
// so the dashes leave the solid line where it ends instead of forking from it.
function futureOf(days: FitnessDay[], forecast: FitnessDay[]): FitnessDay[] {
  const today = days.at(-1);
  return today === undefined ? [] : forecast.filter((d) => d.date > today.date);
}

export const chartDates = (days: FitnessDay[], forecast: FitnessDay[]): string[] => [...days, ...futureOf(days, forecast)].map((d) => d.date);
export const planEnd = (days: FitnessDay[], forecast: FitnessDay[]): string | null => futureOf(days, forecast).at(-1)?.date ?? null;

function continued(days: FitnessDay[], ahead: FitnessDay[], value: (day: FitnessDay) => number): (number | string)[] {
  return [...days.map((d, i) => (i === days.length - 1 ? round1(value(d)) : '-')), ...ahead.map((d) => round1(value(d)))];
}

function planRegion(from: string, to: string, labelled: boolean): Pick<LineSeriesOption, 'markLine' | 'markArea'> {
  const label = { show: labelled, position: 'insideTopLeft' as const, offset: [8, 6], color: colors.textSecondary, fontFamily: chartMono, fontSize: 8 };
  return {
    markLine: { silent: true, symbol: 'none', label: { show: false }, lineStyle: { color: alpha.zeroLine, width: 1, type: 'solid' }, data: [{ xAxis: from }] },
    markArea: { silent: true, itemStyle: { color: alpha.planRegion }, data: [[{ xAxis: from, name: labelled ? 'PLAN →' : '', label }, { xAxis: to }]] },
  };
}

export function forecastLines(days: FitnessDay[], forecast: FitnessDay[]): LineSeriesOption[] {
  const today = days.at(-1);
  const ahead = futureOf(days, forecast);
  const end = ahead.at(-1);
  if (today === undefined || end === undefined) return [];
  const line = (name: string, axis: number, value: (day: FitnessDay) => number, color: string, width: number): LineSeriesOption => ({
    name, type: 'line', xAxisIndex: axis, yAxisIndex: axis, showSymbol: false,
    data: continued(days, ahead, value), lineStyle: { color, width, type: FORECAST_DASH }, itemStyle: { color },
  });
  return [
    {
      ...line('Fitness plan', 0, (d) => d.fitness, colors.ink, 3), ...planRegion(today.date, end.date, true),
      markPoint: { silent: true, data: [dot(end.date, round1(end.fitness), colors.paper, 2, 9)] },
    },
    line('Fatigue plan', 0, (d) => d.fatigue, colors.hot, 2),
    { ...line('Form plan', 1, (d) => d.form, colors.ink, 2), ...planRegion(today.date, end.date, false) },
  ];
}

// Overlaid on the ridden bars (barGap -100%); barMinHeight keeps a 0 day visible as a stub.
export function plannedBars(days: FitnessDay[], forecast: FitnessDay[], variant: Variant = 'desktop'): BarSeriesOption[] {
  if (futureOf(days, forecast).length === 0) return [];
  const planned = new Map(forecast.map((d) => [d.date, Math.round(d.load)]));
  const outline = { color: colors.paper, borderColor: colors.ink, borderWidth: 1 };
  return [{
    name: 'Planned load', type: 'bar', xAxisIndex: 2, yAxisIndex: 2, barGap: '-100%', barMinHeight: 2,
    barCategoryGap: variant === 'phone' ? 2 : '25%',
    data: chartDates(days, forecast).map((date) => {
      const load = planned.get(date);
      if (load === undefined) return '-';
      return load === 0 ? { value: 0, itemStyle: { color: colors.line } } : { value: load, itemStyle: outline };
    }),
  }];
}

// `endLabel` comes from endLabelFits on the measured chart width.
export function fitnessChartOption(days: HistoryDay[], variant: Variant = 'desktop', forecast: FitnessDay[] = [], endLabel = false, sport: SportFilter = 'all'): ECOption {
  const range = formAxisRange([...days, ...futureOf(days, forecast)]);
  return {
    grid: grids(variant),
    xAxis: xAxes(chartDates(days, forecast), variant, days.length - 1, endLabel),
    yAxis: yAxes(range),
    axisPointer: { link: [{ xAxisIndex: 'all' }] },
    tooltip: chartTooltip({ phone: variant === 'phone', formatter: fitnessTooltip(days, forecast, sport) }),
    series: [
      ...fitnessLines(days, variant), formLine(days, range, variant), ...loadBars(days, variant, sport),
      ...forecastLines(days, forecast), ...plannedBars(days, forecast, variant),
    ],
  };
}
